/**
 * @description cgi接口定义
 * @author aprilcai
 */

import { request } from './request';
import { Method } from 'axios';
import { resultCode, resultMessage } from './entity';

let openId: string;
let accessToken: string;
let appCid: string;

const POST: Method = 'post';
const GET: Method = 'get';

interface cgi {
  url: string;
  method: Method;
  beforeRequest: (param?: any) => any;
  afterResponse: (result?: any) => any;
  result?: any;
  param?: any;
  contentType?: string;
  noLogin?: boolean;
}

const globalBeforeRequest = (requestData: any) => ({
  ...requestData,
  ...{
    openid: openId,
    access_token: accessToken,
  },
});

// 接口定义
export const cgis: Map<string, cgi> = new Map(Object.entries({
  // 后台KV存储接口
  accessStore: {
    url: '/common/access_kv_store',
    method: POST,
    result: {
      text_value: '',
    },
    beforeRequest: async (param: any) => {
      if (!openId || !accessToken) {
        await sendRequestService({
          service: 'getUserInfo',
          data: {
            iv_appid: param.appid,
          },
        });
      }
      return param;
    },
    afterResponse: (result: any) => {
      const { code, msg } = result;
      return {
        code,
        msg,
        result: {
          textValue: result.text_value,
        },
      };
    },
  },

  // 道具消费
  consumeProduct: {
    url: '/payaccess/consume_product',
    method: POST,
    result: {},
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg } = result;
      return {
        code,
        msg,
        result: {},
      };
    },
  },

  // 钻石消费
  diamondConsume: {
    url: '/payaccess/diamond_consume',
    method: POST,
    result: {
      balance: 0,
      sequence: '',
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg, balance, sequence } = result;
      return {
        code,
        msg,
        result: {
          balance,
          sequence,
        },
      };
    },
  },

  // 钻石购买道具前的预付费
  diamondPreRecharge: {
    url: '/payaccess/diamond_pre_recharge',
    method: POST,
    result: {
      order_id: '',
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg  } = result;
      return {
        code,
        msg,
        result: {
          orderId: result.order_id,
        },
      };
    },
  },

  // 查询钻石余额
  diamondQueryBalance: {
    url: '/payaccess/diamond_query_balance',
    method: POST,
    result: {
      balance: 0,
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg, balance } = result;
      return {
        code,
        msg,
        result: {
          balance,
        },
      };
    },
  },

  // 查询钻石预付费的日志流水
  diamondQueryRechargeLog: {
    url: '/payaccess/diamond_query_recharge_log',
    method: POST,
    result: {
      flow: [] as any[],
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg, flow } = result;
      return {
        code,
        msg,
        result: {
          flow,
        },
      };
    },
  },

  // 查询道具数量
  queryProduct: {
    url: '/payaccess/query_product',
    method: POST,
    result: {
      product_count: {}, // 一共拥有道具
      product_consumed_count: {}, // 已经消费的道具
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg } = result;
      return {
        code,
        msg,
        result: {
          productCount: result.product_count,
          productConsumedCount: result.product_consumed_count,
        },
      };
    },
  },

  // 获取OAuth2.0的鉴权code
  getAuthCode: {
    url: '/auth/get_auth_code',
    method: POST,
    result: {
      code: '',
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg, authcode } = result;
      return {
        code,
        msg,
        result: {
          code: authcode,
        },
      };
    },
  },

  // 获取用户信息
  getUserInfo: {
    url: '/user/get_user_info',
    method: GET,
    result: {
      type: '',
      openid: '',
      token: '',
      baseInfo: {
        nickname: '',
        avatar: '',
        vip: 0,
      },
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg, nick  } = result;
      openId = result.iv_openid;
      accessToken = result.iv_accesstoken;
      return {
        code,
        msg,
        result: {
          appid: result.iv_appid,
          openid: openId,
          token: accessToken,
          baseInfo: {
            nickname: nick,
            vip: Boolean(result.is_vip),
          },
        },
      };
    },
  },

  // 刷新用户的互动票据
  refreshToken: {
    url: '/user/refresh_token',
    method: GET,
    result: {
      accesstoken: '',
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg } = result;
      accessToken = result.iv_accesstoken;
      return {
        code: code !== 0 ? resultCode.error : 0,
        msg: code !== 0 ? `${resultMessage.error}: ${msg}(${code})` : resultMessage.ok,
        result: {
          accesstoken: accessToken,
        },
      };
    },
  },

  // 查询App对应的Cid
  queryAppCid: {
    url: '/watchrecord/query_app_cid',
    method: POST,
    noLogin: true,
    result: {
      cid: '',
    },
    beforeRequest: (param: any) => {
      if (appCid) {
        return {
          cid: appCid,
          cached: true,
        };
      }
      return param;
    },
    afterResponse: (result: any) => {
      const { code, msg, cid } = result;
      return {
        code,
        msg,
        result: {
          cid: cid || '',
        },
      };
    },
  },

  // 查询观看历史记录
  queryWatchRecord: {
    url: '/watchrecord/query_watch_record',
    method: POST,
    result: {
      watchInfo: {},
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg  } = result;
      return {
        code,
        msg,
        result: {
          watchInfo: result.watch_info,
        },
      };
    },
  },

  // 上报观看历史记录
  reportWatchRecord: {
    url: '/watchrecord/report_watch_record',
    method: POST,
    result: {},
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg } = result;
      return {
        code,
        msg,
        result: {},
      };
    },
  },

  // 获取分享码
  getShareCmd: {
    url: '/vgopen_share/get_share_cmd',
    method: POST,
    result: {
      cmdid: '',
      cmd_text: '',
      friend_cmd_text: '',
      jump_url: '',
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg } = result;
      return {
        code,
        msg,
        result,
      };
    },
  },

  // 上报分享码
  reportShareCmd: {
    url: '/vgopen_share/report_share_cmd',
    method: POST,
    param: {
      openid: '',
      cp_url: '',
      cmd_params: '',
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg } = result;
      return {
        code,
        msg,
        result,
      };
    },
  },

  // 获取分享用户信息
  getShareUsers: {
    url: '/vgopen_share/get_shared_users',
    method: POST,
    result: {
      users: [] as Array<any>,
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg } = result;
      return {
        code,
        msg,
        result,
      };
    },
  },

  // 更新kv值
  setTextValue: {
    url: '/story_tree/set_text_value',
    method: POST,
    contentType: 'json',
    param: {
      drama_id: '',
      text_key: '',
      text_value: '',
      source: '', // 编辑模式下的来源
      preview_session: '', // 编辑模式下额预览session
    },
    result: {},
    beforeRequest: (param: any) => ({
      ...param,
      ...{ text_value: `${param.text_value || ''}` },
    }),
    afterResponse: (result: any) => result,
  },

  // 获取KV值
  getTextValue: {
    url: '/story_tree/get_text_values',
    method: POST,
    contentType: 'json',
    param: {
      drama_id: '',
      text_keys: '',
      source: '', // 编辑模式下的来源
      preview_session: '', // 编辑模式下额预览session
    },
    result: {},
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg  } = result;

      return {
        code,
        msg,
        result: result.text_values,
      };
    },
  },

  // 更新V数值
  updateCurrentNumbers: {
    url: '/story_tree/update_current_numbers',
    method: POST,
    contentType: 'json',
    param: {
      branch_id: '',
      time: '',
      expressions: '',
      source: '', // 编辑模式下的来源
      preview_session: '', // 编辑模式下额预览session
    },
    result: {
      numbers: '',
    },
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg, numbers } = result;
      let fixedNumbers = [];
      if (code === 0) {
        fixedNumbers = numbers.map((number: any) => {
          const { id, name, value } = number;
          return {
            key: id,
            name,
            value,
            id,
          };
        });
      }
      return {
        code,
        msg,
        result: fixedNumbers,
      };
    },
  },

  // 拉取道具列表
  getProductList: {
    url: '/drama_pay/query_drama_product',
    method: GET,
    contentType: 'json',
    param: {
      drama_id: '',
      vid: '',
    },
    result: {},
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg, products } = result;

      return {
        code,
        msg,
        result: products,
      };
    },
  },

  // 单点支付
  pay: {
    url: '/drama_pay/pre_pay',
    method: POST,
    contentType: 'json',
    param: {
      drama_id: '',
      product_id: '',
      product_num: '1',
      timestamp: `${Math.floor(+new Date() / 1000)}`,
      reqtime: `${Math.floor(+new Date() / 1000)}`,
      pf: '2',
    },
    result: {},
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg } = result;

      return {
        code,
        msg,
        result: {
          orderId: result.order_id,
          payRemark: result.pay_remark,
        },
      };
    },
  },

  // 查询支付状态
  getPayState: {
    url: '/drama_pay/query_product_pay_state',
    method: GET,
    contentType: 'json',
    param: {
      drama_id: '',
      product_id: '',
    },
    result: {},
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg } = result;

      return {
        code,
        msg,
        result: {
          orderId: result.order_id,
          payState: result.pay_state,
          orders: result.orders,
        },
      };
    },
  },

  // 排行榜
  getRankList: {
    url: '/iv_service/get_rank_list',
    method: POST,
    contentType: 'json',
    param: {
      metaId: '',
      rankId: '',
      pageNum: 1,
      pageSize: 10,
    },
    result: {},
    beforeRequest: (param: any) => param,
    afterResponse: (result: any) => {
      const { code, msg } = result;

      return {
        code,
        msg,
        result: {
          totalPage: result.totalPage,
          rankList: result.rankList,
        },
      };
    },
  },
}));

/**
 * @description 发起cgi请求，返回对应的数据
 * @param param
 * @param service 对应的cgi接口
 * @param data 请求参数
 * @param isTest 是否请求接口测试环境
 */
export async function sendRequestService(param: {
  service: string;
  data?: any;
  isTest?: boolean;
}): Promise<IResult> {
  const { service, data, isTest } = param;
  const cgi = cgis.get(service);
  const { noLogin, beforeRequest, afterResponse } = cgi;
  const requestData = await beforeRequest(data);
  // 如果该接口数据是可以缓存的，并且已经有缓存，那么直接返回缓存数据
  if (requestData.cached) {
    return cgi.afterResponse(requestData);
  }
  const result = await request({
    isTest,
    data: globalBeforeRequest(requestData),
    cgi,
    noLogin,
  });
  return afterResponse(result);
}
